import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="cookie-policy-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
                alt="AIP Logo" 
                className="h-20 w-auto"
              />
            </Link>
            <Link to="/" className="text-neutral-400 hover:text-white flex items-center gap-2">
              <ArrowLeft size={18} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-2">Cookie Policy</h1>
          <p className="text-neutral-500 mb-8">Last updated: December 2026</p>

          <div className="prose prose-invert max-w-none space-y-8 text-neutral-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your computer, smartphone, or other device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p>
                This Cookie Policy explains how AIP Limited ("we", "us", "our") uses cookies and similar technologies on our website in compliance with the Privacy and Electronic Communications Regulations (PECR) and UK GDPR.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-medium text-white mb-2">Strictly Necessary Cookies</h3>
              <p>
                These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and account access. You cannot opt out of these cookies.
              </p>
              <div className="bg-neutral-900/50 rounded-lg p-4 mt-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-700">
                      <th className="text-left py-2 text-neutral-400">Cookie Name</th>
                      <th className="text-left py-2 text-neutral-400">Purpose</th>
                      <th className="text-left py-2 text-neutral-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-neutral-800">
                      <td className="py-2">client_token</td>
                      <td className="py-2">Authentication for client portal</td>
                      <td className="py-2">7 days</td>
                    </tr>
                    <tr>
                      <td className="py-2">cookie_consent</td>
                      <td className="py-2">Stores your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-medium text-white mb-2 mt-6">Analytics Cookies</h3>
              <p>
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use Google Analytics to analyse website traffic.
              </p>
              <div className="bg-neutral-900/50 rounded-lg p-4 mt-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-700">
                      <th className="text-left py-2 text-neutral-400">Cookie Name</th>
                      <th className="text-left py-2 text-neutral-400">Purpose</th>
                      <th className="text-left py-2 text-neutral-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-neutral-800">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics - distinguishes users</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-neutral-800">
                      <td className="py-2">_ga_*</td>
                      <td className="py-2">Google Analytics - maintains session state</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr>
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics - distinguishes users</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-medium text-white mb-2 mt-6">Functional Cookies</h3>
              <p>
                These cookies enable enhanced functionality and personalisation, such as remembering your preferences and settings.
              </p>
              <div className="bg-neutral-900/50 rounded-lg p-4 mt-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-700">
                      <th className="text-left py-2 text-neutral-400">Cookie Name</th>
                      <th className="text-left py-2 text-neutral-400">Purpose</th>
                      <th className="text-left py-2 text-neutral-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2">client_info</td>
                      <td className="py-2">Stores logged-in client details</td>
                      <td className="py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Third-Party Cookies</h2>
              <p>
                Some cookies on our website are set by third-party services. These include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Google Analytics:</strong> Website traffic analysis and reporting</li>
              </ul>
              <p className="mt-2">
                These third parties have their own privacy policies governing their use of cookies. We recommend reviewing their policies for more information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Managing Cookies</h2>
              <p>
                You can control and manage cookies in several ways. Please note that removing or blocking cookies may impact your user experience and some functionality may no longer be available.
              </p>
              
              <h3 className="text-xl font-medium text-white mb-2 mt-4">Browser Settings</h3>
              <p>
                Most browsers allow you to manage cookie settings. You can typically find these settings in the "Options" or "Preferences" menu. The following links provide information on how to manage cookies in popular browsers:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">Microsoft Edge</a></li>
              </ul>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">Google Analytics Opt-Out</h3>
              <p>
                You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">Google Analytics Opt-out Browser Add-on</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Your Consent</h2>
              <p>
                When you first visit our website, you will be shown a cookie banner asking for your consent to use non-essential cookies. You can change your cookie preferences at any time by clearing your browser cookies and revisiting the site, or by adjusting your browser settings.
              </p>
              <p className="mt-2">
                By continuing to use our website after being presented with the cookie notice, you consent to our use of cookies as described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Contact Us</h2>
              <p>
                If you have any questions about our use of cookies, please contact us:
              </p>
              <p className="mt-2">
                AIP Limited<br />
                Email: <a href="mailto:inventories@aiplimited.co.uk" className="text-[#B8960B] hover:underline">inventories@aiplimited.co.uk</a><br />
                Phone: +44 7841 238 061<br />
                Location: Manchester, United Kingdom
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. More Information</h2>
              <p>
                For more information about cookies and how to manage them, visit <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">www.aboutcookies.org</a> or <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">www.allaboutcookies.org</a>.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;
